// ── PushToRemoteSection — RFC 8628 device auth push flow ───────────────────
import React from 'react';
import {
  Radio,
  Loader2,
  ExternalLink,
  Check,
  ArrowRightLeft,
  AlertTriangle,
  X,
} from 'lucide-react';
import type { EnvironmentDef, ImportResult, ScopeManifest } from '../types';

interface PushToRemoteSectionProps {
  manifest: ScopeManifest;
  targetEnv: string;
  getEnvDef: (key: string) => EnvironmentDef | undefined;
  targetBffUrl: string | null;

  // Device auth state
  devicePhase: string;
  userCode: string | null;
  verificationUri: string | null;
  expiresIn: number | null;
  deviceError: string | null;
  hasCachedSession: (url: string, scope: string) => boolean;

  // Device auth actions
  onPairRemote: () => void;
  onCancelAuth: () => void;
  onResetAuth: () => void;
  onDisconnect: () => void;

  // Push state
  pushStrategy: 'skip' | 'upsert' | 'overwrite';
  setPushStrategy: (v: 'skip' | 'upsert' | 'overwrite') => void;
  pushResult: ImportResult | null;
  setPushResult: (v: ImportResult | null) => void;
  pushError: string | null;
  setPushError: (v: string | null) => void;
  isPushing: boolean;
  filteredManifest: ScopeManifest | null;
  manifestSelectionSize: number;

  // Push action
  onPushManifest: () => void;
}

export function PushToRemoteSection({
  targetEnv,
  getEnvDef,
  targetBffUrl,
  devicePhase,
  userCode,
  verificationUri,
  expiresIn,
  deviceError,
  hasCachedSession,
  onPairRemote,
  onCancelAuth,
  onResetAuth,
  onDisconnect,
  pushStrategy,
  setPushStrategy,
  pushResult,
  setPushResult,
  pushError,
  setPushError,
  isPushing,
  filteredManifest,
  manifestSelectionSize,
  onPushManifest,
}: PushToRemoteSectionProps) {
  return (
    <div className="px-3 py-2.5 border-t border-neutral-800 pac-glass-subtle shrink-0 space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-xs text-neutral-300 font-medium">
          <Radio className="w-3.5 h-3.5 text-violet-400" />
          Push to Remote
        </div>
        {targetBffUrl ? (
          <span
            className="text-[9px] text-neutral-600 font-mono truncate max-w-[180px]"
            title={targetBffUrl}
          >
            {targetBffUrl}
          </span>
        ) : (
          <span className="text-[9px] text-amber-500">
            No bff_base_url for target env
          </span>
        )}
      </div>

      {/* ── Phase: idle — start pairing ── */}
      {devicePhase === 'idle' && !pushResult && (
        <div className="flex items-center gap-2">
          <select
            value={pushStrategy}
            onChange={(e) =>
              setPushStrategy(
                e.target.value as 'skip' | 'upsert' | 'overwrite',
              )
            }
            className="px-2 py-1 rounded pac-glass-input text-neutral-300 text-[10px] outline-none focus:border-violet-500"
          >
            <option value="upsert">Upsert</option>
            <option value="skip">Skip existing</option>
            <option value="overwrite">Overwrite</option>
          </select>
          <button
            disabled={!targetBffUrl}
            onClick={onPairRemote}
            className={
              'flex-1 px-3 py-1.5 rounded border text-xs flex items-center justify-center gap-1.5 transition-colors ' +
              (targetBffUrl
                ? 'border-violet-700 text-violet-200 hover:border-violet-500 hover:bg-violet-950/40'
                : 'border-neutral-800 text-neutral-600 cursor-not-allowed')
            }
          >
            <Radio className="w-3 h-3" />
            {targetBffUrl &&
            hasCachedSession(targetBffUrl, 'scopes.migrate') ? (
              <>Reconnect to {getEnvDef(targetEnv)?.label ?? targetEnv}</>
            ) : (
              <>Pair with {getEnvDef(targetEnv)?.label ?? targetEnv}</>
            )}
          </button>
        </div>
      )}

      {/* ── Phase: requesting ── */}
      {devicePhase === 'requesting' && (
        <div className="flex items-center gap-2 text-xs text-neutral-400">
          <Loader2 className="w-3.5 h-3.5 animate-spin text-violet-400" />
          Connecting…
        </div>
      )}

      {/* ── Phase: awaiting_approval / polling ── */}
      {(devicePhase === 'awaiting_approval' ||
        devicePhase === 'polling') && (
        <div className="border border-violet-800/60 bg-violet-950/30 rounded-lg p-3 space-y-2.5">
          <div className="text-[10px] text-neutral-400">
            Open the remote admin panel and enter this code in{' '}
            <span className="text-violet-300 font-medium">
              Device Approvals
            </span>
            :
          </div>
          <div className="flex items-center justify-center gap-3 py-2">
            <span className="text-2xl font-mono font-bold tracking-[0.25em] text-violet-200 select-all">
              {userCode}
            </span>
          </div>
          <div className="flex items-center justify-between text-[9px]">
            <div className="flex items-center gap-1.5">
              {devicePhase === 'polling' ? (
                <Loader2 className="w-3 h-3 animate-spin text-violet-400" />
              ) : (
                <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
              )}
              <span className="text-neutral-500">
                {devicePhase === 'polling'
                  ? 'Polling for approval…'
                  : 'Waiting for admin approval…'}
              </span>
            </div>
            {expiresIn != null && (
              <span className="text-neutral-600 tabular-nums">
                TTL {Math.floor(expiresIn / 60)}:
                {String(expiresIn % 60).padStart(2, '0')}
              </span>
            )}
          </div>
          {(verificationUri || (userCode && targetBffUrl)) && (
            <a
              href={targetBffUrl && userCode
                ? `/device?user_code=${encodeURIComponent(userCode)}&target=${encodeURIComponent(targetBffUrl)}`
                : (verificationUri ?? '#')}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-[10px] text-violet-400 hover:text-violet-300 transition-colors"
            >
              <ExternalLink className="w-3 h-3" />
              Open verification page
            </a>
          )}
          <button
            onClick={onCancelAuth}
            className="px-2 py-1 rounded border border-neutral-700 text-[10px] text-neutral-400 hover:border-neutral-500 hover:text-neutral-300"
          >
            Cancel
          </button>
        </div>
      )}

      {/* ── Phase: approved — push button ── */}
      {devicePhase === 'approved' && !pushResult && (
        <div className="space-y-2">
          <div className="flex items-center gap-1.5 text-[10px] text-emerald-400">
            <Check className="w-3 h-3" />
            Paired with {getEnvDef(targetEnv)?.label ?? targetEnv}
          </div>
          <div className="flex items-center gap-2">
            <select
              value={pushStrategy}
              onChange={(e) =>
                setPushStrategy(
                  e.target.value as 'skip' | 'upsert' | 'overwrite',
                )
              }
              className="px-2 py-1 rounded pac-glass-input text-neutral-300 text-[10px] outline-none focus:border-violet-500"
            >
              <option value="upsert">Upsert</option>
              <option value="skip">Skip existing</option>
              <option value="overwrite">Overwrite</option>
            </select>
            <button
              disabled={isPushing || !filteredManifest}
              onClick={onPushManifest}
              className={
                'flex-1 px-3 py-1.5 rounded border text-xs flex items-center justify-center gap-1.5 transition-colors ' +
                (isPushing || !filteredManifest
                  ? 'border-neutral-700 text-neutral-500 cursor-wait'
                  : 'border-emerald-700 text-emerald-200 hover:border-emerald-500 hover:bg-emerald-950/40')
              }
            >
              {isPushing ? (
                <>
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Pushing {manifestSelectionSize} scopes…
                </>
              ) : (
                <>
                  <ArrowRightLeft className="w-3 h-3" />
                  Push {manifestSelectionSize} scopes →{' '}
                  {getEnvDef(targetEnv)?.label ?? targetEnv}
                </>
              )}
            </button>
          </div>
          <button
            onClick={onDisconnect}
            className="text-[9px] text-neutral-600 hover:text-neutral-400 transition-colors"
          >
            Disconnect
          </button>
        </div>
      )}

      {/* ── Push result ── */}
      {pushResult && (
        <div className={
          'border rounded p-2.5 space-y-1.5 ' +
          ((pushResult as any)._queued
            ? 'border-amber-800/50 bg-amber-950/30'
            : 'border-emerald-800/50 bg-emerald-950/30')
        }>
          <div className={
            'flex items-center gap-1.5 text-xs font-medium ' +
            ((pushResult as any)._queued ? 'text-amber-300' : 'text-emerald-300')
          }>
            <Check className="w-3.5 h-3.5" />
            {(pushResult as any)._queued
              ? `Queued for approval on ${getEnvDef(targetEnv)?.label ?? targetEnv}`
              : `Pushed to ${getEnvDef(targetEnv)?.label ?? targetEnv}`}
          </div>
          {(pushResult as any)._queued ? (
            <div className="text-[10px] text-amber-400/80 leading-relaxed">
              {(pushResult as any)._queue_message ?? 'Manifest queued for admin approval. An administrator on the remote environment must review and approve this import before it takes effect.'}
            </div>
          ) : (
          <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-[10px]">
            <span className="text-neutral-500">Scopes created</span>
            <span className="text-emerald-400 tabular-nums font-mono">
              {pushResult.scopes_created}
            </span>
            <span className="text-neutral-500">Scopes existing</span>
            <span className="text-neutral-400 tabular-nums font-mono">
              {pushResult.scopes_existing}
            </span>
            <span className="text-neutral-500">Scopes skipped</span>
            <span className="text-neutral-400 tabular-nums font-mono">
              {pushResult.scopes_skipped}
            </span>
            <span className="text-neutral-500">Outcomes written</span>
            <span className="text-sky-400 tabular-nums font-mono">
              {pushResult.outcomes_written}
            </span>
            {(pushResult.outcomes_deleted ?? 0) > 0 && (
              <>
                <span className="text-neutral-500">Outcomes deleted</span>
                <span className="text-rose-400 tabular-nums font-mono">
                  {pushResult.outcomes_deleted}
                </span>
              </>
            )}
            {(pushResult.outcomes_skipped ?? 0) > 0 && (
              <>
                <span className="text-neutral-500">Outcomes skipped</span>
                <span className="text-neutral-500 tabular-nums font-mono">
                  {pushResult.outcomes_skipped}
                </span>
              </>
            )}
            {(pushResult.hot_stream_written ?? 0) > 0 && (
              <>
                <span className="text-neutral-500">Hot stream rows</span>
                <span className="text-amber-400 tabular-nums font-mono">
                  {pushResult.hot_stream_written}
                </span>
              </>
            )}
          </div>
          )}
          <div className="flex items-center gap-2 pt-1">
            <button
              onClick={() => setPushResult(null)}
              className="px-2 py-1 rounded border border-neutral-700 text-[10px] text-neutral-400 hover:border-neutral-500"
            >
              Done
            </button>
            <button
              onClick={() => setPushResult(null)}
              className={
                'px-2 py-1 rounded border text-[10px] ' +
                ((pushResult as any)._queued
                  ? 'border-amber-800 text-amber-300 hover:border-amber-600'
                  : 'border-violet-800 text-violet-300 hover:border-violet-600')
              }
            >
              {(pushResult as any)._queued ? 'Queue again' : 'Push again'}
            </button>
            <button
              onClick={() => {
                setPushResult(null);
                onDisconnect();
              }}
              className="px-2 py-1 rounded border border-neutral-800 text-[10px] text-neutral-600 hover:text-neutral-400 hover:border-neutral-600"
            >
              Disconnect
            </button>
          </div>
        </div>
      )}

      {/* ── Phase: error / expired ── */}
      {(devicePhase === 'error' || devicePhase === 'expired') && (
        <div className="flex items-start gap-1.5 border border-rose-900/50 bg-rose-950/20 rounded p-2">
          <AlertTriangle className="w-3.5 h-3.5 text-rose-500 shrink-0 mt-0.5" />
          <div className="text-[10px] text-rose-400 leading-relaxed">
            {deviceError ?? 'Device auth failed'}
          </div>
          <button
            onClick={onResetAuth}
            className="ml-auto shrink-0 text-[10px] text-neutral-500 hover:text-neutral-300"
          >
            Retry
          </button>
        </div>
      )}

      {/* ── Push error (non-auth) ── */}
      {pushError && (
        <div className="flex items-start gap-1.5 border border-rose-900/50 bg-rose-950/20 rounded p-2">
          <AlertTriangle className="w-3.5 h-3.5 text-rose-500 shrink-0 mt-0.5" />
          <div className="text-[10px] text-rose-400 leading-relaxed">
            {pushError}
          </div>
          <button
            onClick={() => setPushError(null)}
            className="ml-auto shrink-0"
          >
            <X className="w-3 h-3 text-neutral-600 hover:text-neutral-400" />
          </button>
        </div>
      )}
    </div>
  );
}
